import { useState } from 'react';
import {
  serviceList, serviceRefusal, serviceLine, servicePrice, floorDollars,
  nextServiceLineId, professionalServicesBlocker, billingNote,
} from '../lib/professionalServices';

// Cart builder section: Managed Store build fee, Managed AI / AWS retainers,
// On Call / In House care plans (src/lib/professionalServices.js). The rep
// picks a service, types the price where it is typed (and, for a retainer,
// what it costs us a month), and sees the server's refusal live, so a cart
// under the margin floor is caught here rather than on save.
//
// Props: psCatalog (the served `professionalServices` catalog), isInvoice,
// items (to number a new line), onAdd(line), fmt, and the modal's own styles
// so the section looks like its neighbours.
export default function ProfessionalServicesSection({ psCatalog, isInvoice, items, onAdd, fmt, styles }) {
  const { sectionLabel, miniSelect, addBtn } = styles;
  const services = serviceList(psCatalog);
  const [sku, setSku] = useState('');
  const [dollars, setDollars] = useState('');
  const [cost, setCost] = useState('');

  if (!psCatalog) return null;
  const svc = services.find((s) => s.sku === sku) || services[0] || null;
  const blocker = professionalServicesBlocker({ psCatalog, isInvoice });
  const figures = { dollars, cost };
  const refusal = blocker ? null : serviceRefusal(svc, figures, psCatalog.floor);
  const price = servicePrice(svc, figures);
  // Only once a figure is typed: an empty form is not an error yet.
  const typedSomething = svc?.pricing === 'fixed' || dollars !== '' || cost !== '';
  const minimum = svc?.costBasis ? floorDollars(psCatalog.floor, String(cost).replace(/[$,\s]/g, '')) : null;

  function pick(nextSku) {
    setSku(nextSku);
    setDollars('');
    setCost('');
  }

  function add() {
    const line = serviceLine(svc, figures, psCatalog.floor, nextServiceLineId(items, svc.sku));
    if (!line) return;
    onAdd(line);
    setDollars('');
    setCost('');
  }

  const cadence = (s) => (s.billing === 'monthly' ? '/mo' : ' one-time');
  const input = { ...miniSelect, fontSize: 12, padding: '6px 8px', width: '100%', boxSizing: 'border-box' };
  const fieldLabel = { fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)', textTransform: 'uppercase', marginBottom: 2 };

  return (
    <>
      <div style={sectionLabel}>Professional services</div>
      <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10, background: 'var(--bg3)', marginBottom: 14 }}>
        {blocker ? (
          <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.4 }}>{blocker}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <select value={svc?.sku || ''} onChange={(e) => pick(e.target.value)} style={input}>
              {services.map((s) => (
                <option key={s.sku} value={s.sku}>
                  {s.label}{s.pricing === 'fixed' ? ` · ${fmt(s.monthlyDollars)}${cadence(s)}` : ` · typed${cadence(s)}`}
                </option>
              ))}
            </select>

            {svc?.pricing === 'typed' && (
              <div style={{ display: 'flex', gap: 6 }}>
                <label style={{ flex: 1 }}>
                  <div style={fieldLabel}>{svc.billing === 'monthly' ? 'Price / month' : 'Build fee'}</div>
                  <input
                    inputMode="numeric"
                    placeholder={`$${svc.minDollars.toLocaleString('en-US')}+`}
                    value={dollars}
                    onChange={(e) => setDollars(e.target.value)}
                    style={input}
                  />
                </label>
                {svc.costBasis && (
                  <label style={{ flex: 1 }}>
                    <div style={fieldLabel}>Our cost / month</div>
                    <input
                      inputMode="decimal"
                      placeholder="0 if none"
                      value={cost}
                      onChange={(e) => setCost(e.target.value)}
                      style={input}
                      title="Internal. Checked against the margin floor; never shown to the customer."
                    />
                  </label>
                )}
              </div>
            )}

            {svc?.costBasis && minimum != null && cost !== '' && (
              <div style={{ fontSize: 10, color: 'var(--text3)', fontFamily: 'var(--mono)' }}>
                Floor at this cost: {fmt(minimum)}/mo
              </div>
            )}

            {refusal && typedSomething && (
              <div role="alert" style={{ fontSize: 11, color: 'var(--red, #f87171)', lineHeight: 1.4 }}>{refusal}</div>
            )}

            <button onClick={add} disabled={!!refusal} style={{ ...addBtn, width: '100%', marginTop: 2, opacity: refusal ? 0.5 : 1 }}>
              + Add {svc?.label}{price != null && !refusal ? ` · ${fmt(price)}${cadence(svc)}` : ''}
            </button>
          </div>
        )}
        <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 6, lineHeight: 1.4 }}>
          {blocker ? 'Staff carts only.' : billingNote(svc)}
        </div>
      </div>
    </>
  );
}
